import { callClaude, servidoPorPlan } from './claude.js';
export class DailyCapExceededError extends Error {
    todaySpend;
    cap;
    name = 'DailyCapExceededError';
    constructor(todaySpend, cap) {
        super(`Daily AI cap of $${cap.toFixed(2)} reached (spent $${todaySpend.toFixed(4)} today)`);
        this.todaySpend = todaySpend;
        this.cap = cap;
    }
}
/**
 * Error de un modelo sin precio en la tabla.
 *
 * Se prefiere romper antes que estimar: la estimación alimenta el tope diario
 * de gasto Y lo que se le factura al cliente por consumo. Un número inventado
 * ahí no se nota hasta que alguien mira la factura.
 */
export class UnknownModelPricingError extends Error {
    model;
    constructor(model) {
        super(`Sin precio para el modelo "${model}". Agregalo a MODEL_PRICING ` +
            `(packages/shared/src/claude/MeteredClaudeClient.ts) con la tarifa de la ` +
            `lista de precios de Anthropic antes de usarlo.`);
        this.model = model;
        this.name = 'UnknownModelPricingError';
    }
}
// Precio por millón de tokens, entrada/salida — actualizar cuando Anthropic
// cambie las tarifas.
//
// TIENE QUE ESTAR TODO MODELO QUE EL CÓDIGO USE. Antes, un modelo ausente caía
// a la tarifa de Opus ($15/$75) sin decir nada, y eso registró 5× de más
// durante semanas en tres features que ya habían pasado a Sonnet 5 y Sonnet 4.5
// (medido 2026-09-02: $1,12 registrados contra $0,22 reales). El daño no es la
// plata —eran centavos— sino que el tope diario se consumía cinco veces más
// rápido y el panel de costo por cuenta mentía, que es lo que sostiene el cobro
// por consumo. `modelPricingTable.test.ts` falla si aparece un modelo en el
// código que no esté acá.
//
// Revisado contra la tabla de Anthropic el 2026-10-20: Sonnet 5 figuraba a
// $3/$15 (es $2/$10) y los Opus 4.7/5 a $15/$75 (son $5/$25). El panel de
// consumo de Villa Sión, que es lo que se le factura, marcaba ~50% de más.
// Haiku 5.5: $0,10/$0,50 con prompts de hasta 100K tokens (encima, $0,50/$2,50;
// los de Evo Hub no llegan).
const MODEL_PRICING = {
    'claude-haiku-4-5-20251001': { inputPer1M: 1.00, outputPer1M: 5.00 },
    'claude-haiku-5-5': { inputPer1M: 0.10, outputPer1M: 0.50 },
    'claude-sonnet-4-5-20250929': { inputPer1M: 3.00, outputPer1M: 15.00 },
    'claude-sonnet-4-6': { inputPer1M: 3.00, outputPer1M: 15.00 },
    'claude-sonnet-5': { inputPer1M: 2.00, outputPer1M: 10.00 },
    'claude-opus-4-7': { inputPer1M: 5.00, outputPer1M: 25.00 },
    'claude-opus-5': { inputPer1M: 5.00, outputPer1M: 25.00 },
};
/** Precio del modelo, o error. Nunca un default. */
function pricingFor(model) {
    const pricing = MODEL_PRICING[model];
    if (!pricing)
        throw new UnknownModelPricingError(model);
    return pricing;
}
/** Los modelos con precio conocido — para que un chequeo externo los liste. */
export function modelsWithPricing() {
    return Object.keys(MODEL_PRICING);
}
function estimateCost(model, inputTokens, outputTokens) {
    const pricing = pricingFor(model);
    return (inputTokens / 1_000_000) * pricing.inputPer1M +
        (outputTokens / 1_000_000) * pricing.outputPer1M;
}
// ── Lo que cuesta el caché ───────────────────────────────────────────────────
// Escribir una entrada cuesta MÁS que mandar el texto sin cachear; leerla,
// mucho menos. Los multiplicadores son los de la API.
//
// Esto no es un detalle contable: `response.usage.input_tokens` NO incluye los
// tokens cacheados, así que un cliente que solo mire ese campo registraría un
// gasto menor al real y creería que el caché ahorró más de lo que ahorró. La
// medición del ahorro depende de contar bien las tres partes.
const _CACHE_WRITE_MULT = 1.25;
const _CACHE_READ_MULT = 0.1;
/** Costo de una respuesta, contando las tres clases de token de entrada. */
export function costOfUsage(model, usage) {
    const pricing = pricingFor(model);
    const escritos = usage.cache_creation_input_tokens ?? 0;
    const leidos = usage.cache_read_input_tokens ?? 0;
    return (usage.input_tokens / 1_000_000) * pricing.inputPer1M
        + (escritos / 1_000_000) * pricing.inputPer1M * _CACHE_WRITE_MULT
        + (leidos / 1_000_000) * pricing.inputPer1M * _CACHE_READ_MULT
        + (usage.output_tokens / 1_000_000) * pricing.outputPer1M;
}
/** Public cost estimator — used by callers that need to accumulate spend across
 *  multiple metered calls (e.g. the assistant's per-message budget guard). */
export function estimateCostUsd(model, inputTokens, outputTokens) {
    return estimateCost(model, inputTokens, outputTokens);
}
// ── Cap-check serialization ───────────────────────────────────────────────────
// The cap check is read-then-act: concurrent calls could all read the same
// today-spend and pass together. Checks run one at a time through _capLock, and
// each in-flight call reserves its estimated cost in _inFlightUsd until its
// real usage is recorded, so parallel calls can't overshoot the cap on a stale
// read. Module-level on purpose: clients are constructed per request.
let _inFlightUsd = 0;
let _capLock = Promise.resolve();
function withCapLock(fn) {
    const run = _capLock.then(fn, fn);
    _capLock = run.catch(() => undefined);
    return run;
}
export class MeteredClaudeClient {
    apiKey;
    dailyCapUsd;
    persistence;
    constructor(opts) {
        this.apiKey = opts.apiKey;
        this.dailyCapUsd = opts.dailyCapUsd ?? 5.00;
        this.persistence = opts.persistence;
    }
    today() {
        return new Date().toISOString().slice(0, 10);
    }
    async getTodaySpend() {
        return this.persistence.getTodaySpend(this.today());
    }
    async getRemaining() {
        const spent = await this.getTodaySpend();
        return Math.max(0, this.dailyCapUsd - spent);
    }
    async messages(params) {
        const { feature, timeoutMs, requestLabel, cacheStablePrefix, cacheFirstUserMessage, stream: _stream, ...restParams } = params;
        const model = restParams.model;
        // Estimate cost upfront using a rough guess (assume 200 output tokens if unknown)
        const inputEst = this.countEstimatedInputTokens(restParams);
        const estimatedCost = estimateCost(model, inputEst, 200);
        // Serialized check + reservation (see _capLock above)
        await withCapLock(async () => {
            const todaySpend = await this.getTodaySpend();
            if (todaySpend + _inFlightUsd + estimatedCost > this.dailyCapUsd) {
                throw new DailyCapExceededError(todaySpend, this.dailyCapUsd);
            }
            _inFlightUsd += estimatedCost;
        });
        try {
            const response = await callClaude({
                ...restParams,
                apiKey: this.apiKey,
                requestLabel: requestLabel ?? `metered.${feature}`,
                ...(timeoutMs != null ? { timeoutMs } : {}),
                ...(cacheStablePrefix === true ? { cacheStablePrefix: true } : {}),
                ...(cacheFirstUserMessage === true ? { cacheFirstUserMessage: true } : {}),
            });
            const inputTokens = response.usage.input_tokens;
            const outputTokens = response.usage.output_tokens;
            // Las tres clases de token, no solo input_tokens: ver costOfUsage.
            const u = response.usage;
            const cacheWriteTokens = u.cache_creation_input_tokens ?? 0;
            const cacheReadTokens = u.cache_read_input_tokens ?? 0;
            // Lo que pagó el plan no cuesta por llamada: ni suma al tope diario ni
            // se le factura a nadie. Los tokens se registran igual.
            const viaPlan = servidoPorPlan(response);
            const costUsd = viaPlan ? 0 : costOfUsage(model, u);
            await this.persistence.recordUsage({
                date: this.today(),
                model,
                inputTokens,
                outputTokens,
                costUsd,
                feature,
                ...(cacheWriteTokens > 0 ? { cacheWriteTokens } : {}),
                ...(cacheReadTokens > 0 ? { cacheReadTokens } : {}),
                ...(viaPlan ? { viaPlan: true } : {}),
            });
            return response;
        }
        finally {
            _inFlightUsd -= estimatedCost;
        }
    }
    countEstimatedInputTokens(params) {
        // ~4 chars per token, rough estimate for cap pre-check.
        // Includes system prompt and tool schemas — in tool-loop calls those are a
        // large share of the real input and skipping them under-counted the margin.
        const msgChars = params.messages.reduce((acc, m) => {
            const content = typeof m.content === 'string' ? m.content : JSON.stringify(m.content);
            return acc + content.length;
        }, 0);
        const sysChars = params.system == null
            ? 0
            : (typeof params.system === 'string' ? params.system : JSON.stringify(params.system)).length;
        const toolChars = params.tools == null ? 0 : JSON.stringify(params.tools).length;
        return Math.ceil((msgChars + sysChars + toolChars) / 4);
    }
}
//# sourceMappingURL=MeteredClaudeClient.js.map